import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Shield, AlertTriangle, CheckCircle2, TrendingUp, FileText, Loader2, RefreshCw,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NachaDashboard = ({ token, businessId }) => {
  const [overview, setOverview] = useState(null);
  const [returnRates, setReturnRates] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const { toast } = useToast();

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API}/api/mybookie/admin/nacha/overview`, authHeaders),
        axios.get(`${API}/api/mybookie/nacha/annual-reviews`, authHeaders),
      ];
      if (businessId) {
        promises.push(axios.get(`${API}/api/mybookie/nacha/return-rates/${businessId}?months=6`, authHeaders));
      }
      const results = await Promise.all(promises);
      setOverview(results[0].data);
      setReviews(results[1].data.reviews || []);
      if (results[2]) setReturnRates(results[2].data);
    } catch (err) {
      console.error("NACHA dashboard error:", err);
    } finally { setLoading(false); }
  }, [token, businessId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateReview = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/nacha/annual-review`, { notes: reviewNotes }, authHeaders);
      toast({ title: "Annual review generated", description: `NACHA compliance review for ${res.data.year}` });
      setShowReviewDialog(false);
      setReviewNotes('');
      fetchData();
    } catch (err) {
      console.error("Annual review error:", err);
      toast({ title: "Error", description: "Failed to generate review", variant: "destructive" });
    } finally { setGenerating(false); }
  };

  const pct = (v) => `${((v || 0) * 100).toFixed(2)}%`;
  const rateColor = (rate, threshold) => rate > threshold ? 'text-red-600' : rate > threshold * 0.7 ? 'text-amber-600' : 'text-green-600';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-5 h-5 animate-spin text-[#0A2B1D] mr-2" />
        <span className="text-[#5C6661]">Loading NACHA compliance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nacha-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#111614] flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0A2B1D]" /> NACHA ACH Compliance
          </h2>
          <p className="text-sm text-[#5C6661]">Fraud monitoring, return rate tracking, and annual reviews (2026 rules)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="rounded-sm" data-testid="refresh-nacha">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowReviewDialog(true)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="generate-review-btn">
            <FileText className="w-3.5 h-3.5 mr-1" /> Generate Annual Review
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-[#F6F5F2] p-1 rounded-sm">
        {[
          { id: 'overview', label: 'Fraud Monitoring', icon: Shield },
          { id: 'returns', label: 'Return Rates', icon: TrendingUp },
          { id: 'reviews', label: 'Annual Reviews', icon: FileText },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveView(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-sm rounded-sm transition-colors ${
              activeView === id ? 'bg-white text-[#0A2B1D] font-medium shadow-sm' : 'text-[#5C6661] hover:text-[#111614]'
            }`} data-testid={`nacha-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW / FRAUD MONITORING ===== */}
      {activeView === 'overview' && overview && (
        <>
          {/* Compliance Status Banner */}
          <div className={`rounded border p-4 flex items-center gap-3 ${overview.nacha_compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {overview.nacha_compliant ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            )}
            <div>
              <p className={`font-medium text-sm ${overview.nacha_compliant ? 'text-green-700' : 'text-red-700'}`}>
                {overview.nacha_compliant ? 'NACHA Compliant' : 'NACHA Compliance Issue Detected'}
              </p>
              <p className="text-xs text-[#5C6661]">Based on return rates from the last 30 days</p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">ACH Transactions (30d)</p>
                <p className="text-xl font-semibold text-[#111614]" data-testid="nacha-total-txns">{overview.total_transactions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Total Returns</p>
                <p className="text-xl font-semibold text-red-600">{overview.total_returns}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Unauthorized Rate</p>
                <p className={`text-xl font-semibold ${rateColor(overview.unauthorized_return_rate, 0.005)}`} data-testid="nacha-unauth-rate">
                  {pct(overview.unauthorized_return_rate)}
                </p>
                <p className="text-[10px] text-[#8A948F]">Threshold: 0.50%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Held / Flagged</p>
                <p className="text-xl font-semibold text-amber-600">{overview.held_transactions} / {overview.flagged_transactions}</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent High-Risk Assessments */}
          {overview.recent_high_risk?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Recent High-Risk Assessments</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-[#F6F5F2]">
                      <th className="text-left py-2 px-3 text-xs">Date</th>
                      <th className="text-right py-2 px-3 text-xs">Amount</th>
                      <th className="text-center py-2 px-3 text-xs">Score</th>
                      <th className="text-center py-2 px-3 text-xs">Action</th>
                      <th className="text-left py-2 px-3 text-xs">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.recent_high_risk.map((a, idx) => (
                      <tr key={`${a.created_at}-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 text-xs text-[#5C6661]">{a.created_at?.slice(0, 10)}</td>
                        <td className="py-2 px-3 text-right">${(a.amount || 0).toLocaleString()}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            a.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>{a.score}</span>
                        </td>
                        <td className="py-2 px-3 text-center text-xs font-medium uppercase">{a.action}</td>
                        <td className="py-2 px-3 text-xs text-[#5C6661]">{(a.flags || []).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== RETURN RATES ===== */}
      {activeView === 'returns' && (
        <>
          {returnRates ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5C6661]">Total Transactions</p>
                    <p className="text-lg font-semibold">{returnRates.total_transactions}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5C6661]">Overall Return Rate</p>
                    <p className={`text-lg font-semibold ${rateColor(returnRates.overall_return_rate, 0.03)}`}>
                      {pct(returnRates.overall_return_rate)}
                    </p>
                    <p className="text-[10px] text-[#8A948F]">Max: 3.0%</p>
                  </CardContent>
                </Card>
                <Card className={returnRates.unauthorized_return_rate > 0.005 ? 'border-red-300 bg-red-50' : ''}>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5C6661]">Unauthorized Rate</p>
                    <p className={`text-lg font-semibold ${rateColor(returnRates.unauthorized_return_rate, 0.005)}`}>
                      {pct(returnRates.unauthorized_return_rate)}
                    </p>
                    <p className="text-[10px] text-[#8A948F]">Max: 0.5%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5C6661]">Admin Rate</p>
                    <p className="text-lg font-semibold">{pct(returnRates.admin_return_rate)}</p>
                  </CardContent>
                </Card>
                <Card className={returnRates.nacha_compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  <CardContent className="p-4">
                    <p className="text-xs text-[#5C6661]">Status</p>
                    <p className={`text-lg font-semibold ${returnRates.nacha_compliant ? 'text-green-600' : 'text-red-600'}`}>
                      {returnRates.nacha_compliant ? 'Compliant' : 'Non-Compliant'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts */}
              {returnRates.alerts?.length > 0 && (
                <div className="space-y-2">
                  {returnRates.alerts.map((a, idx) => (
                    <div key={`return-alert-${idx}-${a.slice(0,15)}`} className={`rounded border p-3 flex items-start gap-2 ${
                      a.startsWith('CRITICAL') ? 'bg-red-50 border-red-200' : a.startsWith('HIGH') ? 'bg-amber-50 border-amber-200' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#111614]">{a}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Returns by Code */}
              {returnRates.by_return_code?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Returns by Code</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-[#F6F5F2]">
                          <th className="text-left py-2 px-3 text-xs">Code</th>
                          <th className="text-left py-2 px-3 text-xs">Description</th>
                          <th className="text-right py-2 px-3 text-xs">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnRates.by_return_code.map(r => (
                          <tr key={r.code} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono font-medium">{r.code}</td>
                            <td className="py-2 px-3 text-[#5C6661]">{r.description}</td>
                            <td className="py-2 px-3 text-right font-medium">{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Trend */}
              {returnRates.monthly_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Monthly Return Rate Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-[#F6F5F2]">
                          <th className="text-left py-2 px-3 text-xs">Month</th>
                          <th className="text-right py-2 px-3 text-xs">Transactions</th>
                          <th className="text-right py-2 px-3 text-xs">Returns</th>
                          <th className="text-right py-2 px-3 text-xs">Unauthorized</th>
                          <th className="text-right py-2 px-3 text-xs">Return Rate</th>
                          <th className="text-right py-2 px-3 text-xs">Unauth Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnRates.monthly_trend.map(m => (
                          <tr key={m.month} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{m.month}</td>
                            <td className="py-2 px-3 text-right">{m.transactions}</td>
                            <td className="py-2 px-3 text-right text-red-600">{m.returns}</td>
                            <td className="py-2 px-3 text-right text-red-700">{m.unauthorized}</td>
                            <td className={`py-2 px-3 text-right font-medium ${rateColor(m.return_rate, 0.03)}`}>{pct(m.return_rate)}</td>
                            <td className={`py-2 px-3 text-right font-medium ${rateColor(m.unauthorized_rate, 0.005)}`}>{pct(m.unauthorized_rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <p className="text-sm text-[#5C6661] text-center py-8">Select a business to view return rates, or no ACH data available yet.</p>
          )}
        </>
      )}

      {/* ===== ANNUAL REVIEWS ===== */}
      {activeView === 'reviews' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">NACHA Annual Compliance Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-8 h-8 mx-auto mb-2 text-[#B4C9BE]" />
                <p className="text-sm text-[#5C6661]">No annual reviews yet. Generate your first review to document NACHA compliance.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={`review-${r.year}-${r.review_date}`} className="border rounded p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-sm">{r.year} Annual Review</h3>
                        <p className="text-xs text-[#8A948F]">Reviewed: {r.review_date?.slice(0, 10)} by {r.reviewed_by}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.nacha_compliant ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {r.nacha_compliant ? 'Compliant' : 'Non-Compliant'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                      <div className="bg-[#F6F5F2] rounded p-2 text-center">
                        <p className="text-[10px] text-[#8A948F]">ACH Transactions</p>
                        <p className="font-medium">{r.total_ach_transactions}</p>
                      </div>
                      <div className="bg-[#F6F5F2] rounded p-2 text-center">
                        <p className="text-[10px] text-[#8A948F]">Returns</p>
                        <p className="font-medium">{r.total_returns}</p>
                      </div>
                      <div className="bg-[#F6F5F2] rounded p-2 text-center">
                        <p className="text-[10px] text-[#8A948F]">Unauthorized Rate</p>
                        <p className={`font-medium ${rateColor(r.unauthorized_return_rate, 0.005)}`}>{pct(r.unauthorized_return_rate)}</p>
                      </div>
                      <div className="bg-[#F6F5F2] rounded p-2 text-center">
                        <p className="text-[10px] text-[#8A948F]">Held/Flagged</p>
                        <p className="font-medium">{r.transactions_held}/{r.transactions_flagged}</p>
                      </div>
                    </div>
                    {/* Fraud Controls Status */}
                    {r.fraud_controls && (
                      <details className="mt-2">
                        <summary className="text-xs text-[#0A2B1D] cursor-pointer font-medium">Fraud Controls Status</summary>
                        <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                          {Object.entries(r.fraud_controls).map(([k, v]) => (
                            <div key={k} className="flex justify-between bg-[#F6F5F2] rounded px-2 py-1">
                              <span className="text-[#5C6661]">{k.replace(/_/g, ' ')}</span>
                              <span className="text-green-600 font-medium">{v}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {r.recommendations?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {r.recommendations.map((rec, idx) => (
                          <p key={`rec-${r.year}-${idx}`} className="text-xs text-amber-700 flex items-start gap-1.5">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {rec}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Generate Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent data-testid="annual-review-dialog">
          <DialogHeader><DialogTitle>Generate NACHA Annual Compliance Review</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5C6661]">
              This will generate a documented annual review of all ACH fraud controls, return rates,
              and compliance metrics as required by NACHA 2026 rules.
            </p>
            <div>
              <Label>Additional Notes (optional)</Label>
              <Input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                placeholder="E.g., Reviewed with legal team, updated velocity thresholds..."
                data-testid="review-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleGenerateReview} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={generating} data-testid="submit-review-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { NachaDashboard };
export default NachaDashboard;
